/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import { Table, VisuallyHidden } from '@radix-ui/themes';

import { SortableTableHeader, type SortDirection } from '../../../../shared/SortableTableHeader';
import { SeverityBadge } from './SeverityBadge';
import { QUARANTINE_STRINGS as STRINGS } from './quarantine.strings';
import type { QuarantinedComponent, QuarantineSortField } from './quarantine.types';

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

const COLUMNS: { sortKey: QuarantineSortField; label: string }[] = [
  { sortKey: 'SEVERITY', label: STRINGS.columns.severity },
  { sortKey: 'COMPONENT', label: STRINGS.columns.component },
  { sortKey: 'REPOSITORY', label: STRINGS.columns.repository },
  { sortKey: 'POLICY', label: STRINGS.columns.policy },
  { sortKey: 'QUARANTINE_TIME', label: STRINGS.columns.quarantineTime },
];

function formatTime(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : TIME_FORMAT.format(date);
}

export interface QuarantineTableProps {
  items: QuarantinedComponent[];
  sortBy: QuarantineSortField;
  asc: boolean;
  onSort: (sortBy: QuarantineSortField, asc: boolean) => void;
}

export function QuarantineTable({ items, sortBy, asc, onSort }: QuarantineTableProps): JSX.Element {
  const handleSort = (key: string, direction: SortDirection) => onSort(key as QuarantineSortField, direction === 'asc');

  return (
    <Table.Root variant="surface" size="2">
      <caption>
        <VisuallyHidden>Quarantined components</VisuallyHidden>
      </caption>
      <Table.Header>
        <Table.Row>
          {COLUMNS.map(({ sortKey, label }) => (
            <SortableTableHeader
              key={sortKey}
              sortKey={sortKey}
              currentSortKey={sortBy}
              currentSortDirection={asc ? 'asc' : 'desc'}
              onSort={handleSort}
            >
              {label}
            </SortableTableHeader>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {items.map((item) => (
          <Table.Row key={`${item.key}:${item.policyName}`}>
            <Table.Cell>
              <SeverityBadge threatLevel={item.threatLevel} severity={item.severity} />
            </Table.Cell>
            <Table.Cell>{item.displayName}</Table.Cell>
            <Table.Cell>{item.repositoryName}</Table.Cell>
            <Table.Cell>{item.policyName}</Table.Cell>
            <Table.Cell>{formatTime(item.quarantineTime)}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
